/**
 * Column definitions for the Contract Negotiations list table.
 * Mirrors legacy list columns 1-1: code (link), title, status, priority, type,
 * stage, value, progress, assignees, creator, deadline, created_at.
 */
import type { ColumnDef } from '@vnetwork/ui/data';
import { StatusPill } from '@vnetwork/ui/data';
import { Badge, Chip, Progress } from '@vnetwork/ui/primitives';
import { TableLink } from '@/components/table-link';
import { formatDate, formatMoney } from '@/lib/format';
import type { ContractNegotiationRow } from '@/api/contract-negotiations';
import {
  negotiationStatusTone,
  negotiationStatusLabel,
  negotiationPriorityTone,
  negotiationPriorityLabel,
  negotiationTypeLabel,
  negotiationStageLabel,
} from './negotiation-format';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;
const right = { display: 'block', textAlign: 'right' as const };

export type NegotiationCol = ColumnDef<ContractNegotiationRow, unknown>;

const rightHeader = (label: string) => () => (
  <span style={right}>{label}</span>
);

export const negotiationColumns: NegotiationCol[] = [
  {
    id: 'contract_negotiation_code',
    header: 'Code',
    cell: ({ row }) => {
      const r = row.original;
      const code = r.contract_negotiation_code || `#${r.contract_negotiation_id}`;
      return (
        <TableLink to={`/contracts/negotiations/${r.contract_negotiation_id}`} title={code}>
          {code}
        </TableLink>
      );
    },
  },
  {
    id: 'contract_negotiation_title',
    header: 'Title',
    cell: ({ row }) => {
      const title = row.original.contract_negotiation_title;
      if (!title) return <span style={muted}>—</span>;
      return (
        <span
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={title}
        >
          {title}
        </span>
      );
    },
  },
  {
    id: 'contract_negotiation_status',
    header: 'Status',
    cell: ({ row }) => (
      <StatusPill tone={negotiationStatusTone(row.original.contract_negotiation_status)}>
        {negotiationStatusLabel(row.original.contract_negotiation_status)}
      </StatusPill>
    ),
  },
  {
    id: 'contract_negotiation_priority',
    header: 'Priority',
    cell: ({ row }) => {
      const p = row.original.contract_negotiation_priority;
      if (!p) return <span style={muted}>—</span>;
      return (
        <Chip appearance="subtle" tone={negotiationPriorityTone(p)} size="sm">
          {negotiationPriorityLabel(p)}
        </Chip>
      );
    },
  },
  {
    id: 'contract_negotiation_type',
    header: 'Type',
    cell: ({ row }) => {
      const t = row.original.contract_negotiation_type;
      if (!t) return <span style={muted}>—</span>;
      return <Badge tone="neutral">{negotiationTypeLabel(t)}</Badge>;
    },
  },
  {
    id: 'contract_negotiation_stage',
    header: 'Stage',
    cell: ({ row }) => {
      const s = row.original.contract_negotiation_stage;
      if (!s) return <span style={muted}>—</span>;
      return <Badge tone="info">{negotiationStageLabel(s)}</Badge>;
    },
  },
  {
    id: 'customer_name',
    header: 'Customer',
    cell: ({ row }) => {
      const name = row.original.company_name || row.original.customer_name;
      if (!name) return <span style={muted}>—</span>;
      return (
        <span
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={name}
        >
          {name}
        </span>
      );
    },
  },
  {
    id: 'contract_negotiation_value',
    header: rightHeader('Value'),
    cell: ({ row }) => (
      <span style={{ ...right, fontVariantNumeric: 'tabular-nums' }}>
        {formatMoney(row.original.contract_negotiation_value)}
      </span>
    ),
  },
  {
    id: 'contract_negotiation_progress',
    header: 'Progress',
    cell: ({ row }) => {
      const pct = row.original.contract_negotiation_progress ?? 0;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 80 }}>
          <Progress value={pct} max={100} style={{ flex: 1 }} />
          <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', whiteSpace: 'nowrap' }}>
            {pct}%
          </span>
        </div>
      );
    },
  },
  {
    id: 'assignee_names',
    header: 'Assignees',
    cell: ({ row }) => {
      const names = row.original.assignee_names;
      if (!names || names.length === 0) return <span style={muted}>—</span>;
      const label = names.join(', ');
      return (
        <span
          style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 'var(--vn-font-size-xs)' }}
          title={label}
        >
          {label}
        </span>
      );
    },
  },
  {
    id: 'creator_name',
    header: 'Creator',
    cell: ({ row }) => {
      const name = row.original.creator_name;
      if (!name) return <span style={muted}>—</span>;
      return <span style={{ whiteSpace: 'nowrap' }}>{name}</span>;
    },
  },
  {
    id: 'contract_negotiation_deadline',
    header: rightHeader('Deadline'),
    cell: ({ row }) => (
      <span style={{ ...right, whiteSpace: 'nowrap' }}>
        {formatDate(row.original.contract_negotiation_deadline)}
      </span>
    ),
  },
  {
    id: 'created_at',
    header: rightHeader('Created'),
    cell: ({ row }) => (
      <span style={{ ...right, whiteSpace: 'nowrap' }}>
        {formatDate(row.original.created_at)}
      </span>
    ),
  },
];
